import React, { useContext, useState, useEffect, useMemo } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Tabs } from 'foundations-library/components'
import API from 'api'
import { MyPhoneContactContext, UserContext } from 'providers'
import { incomingCall } from './CallContent'
import CallDetailsTabsSkeleton from './CallDetailsTabsSkeleton'
import CallSummary from './CallSummary'
import CallTranscript from './CallTranscription'

const useStyles = makeStyles((theme) => {
    const { colors } = theme['foundations-library']

    return {
        root: {
            width: '100%',
            '& .tabs': {
                backgroundColor: colors.neutral[50],
                '& .tab': {
                    backgroundColor: 'transparent',
                    color: colors.neutral[700],
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    '&.active--tab': {
                        backgroundColor: `${colors.neutral[900]}!important`,
                        color: '#fff'
                    }
                }
            }
        }
    }
})

/**
 *
 */
export const tabs = {
    summary: {
        text: 'Call Summary & Action Items'
    },
    transcript: {
        text: 'Transcript'
    }
}

type Call = Record<string, any>

const getRecordingUUID = (call: Call) => {
    let UUID = ''
    const id = call.id
    if (id) UUID = id.split('|')[0]
    return UUID
}

/**
 *
 */
const CallDetailsTabs = ({ call, doesOtherContactExist = false }: { call: Call, doesOtherContactExist?: boolean }): JSX.Element | null => {
    const classes = useStyles()
    const [activeTab, setActiveTab] = useState('summary')
    const [transcription, setTranscription] = useState<Record<string, any> | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const contactContext = useContext(MyPhoneContactContext)
    const userInfo = useContext(UserContext)
    const UUID = useMemo(() => getRecordingUUID(call), [call.id])
    const [numbersToContactMapState, setNumbersToContactMapState] = useState<Map<string, string>>(new Map<string, string>())

    const getContactName = (number: string) => {
        if (!isOtherContact(number)) return `${userInfo.first_name} ${userInfo.last_name}`

        return numbersToContactMapState.get(number) || number
    }

    const isOtherContact = (number: string) => {
        const myNumber = call.type === incomingCall ? call.to.number : call.from.number
        if (number === myNumber) return false
        return true
    }

    useEffect(() => {
        if (!UUID) return

        const numbersToContactMap = new Map<string, string>()

        const replaceNumbersWithName = (value: string) => {
            numbersToContactMap.forEach((displayName, number) => {
                const escapedNumber = number.replace(/[+.]/g, '\\$&')
                value = value.replace(new RegExp(escapedNumber, 'gi'), `<br/> **${displayName}** *(${number})*`)
            })
            return value
        }

        const filteredContent = (content: Record<string, any> | string): Record<string, any> | string => {
            if (typeof content === 'string') {
                return replaceNumbersWithName(content)
            } else if (typeof content === 'object') {
                return Object.fromEntries(Object.entries(content || {}).map(([key, value]: [string, any]) => {
                    return [key, key === 'transcription_object' ? value : filteredContent(value)]
                }))
            }
            return content
        }

        const findE164Numbers = (data: Record<string, any>): string[] => {
            const regex = /\+[\d]{10,15}/g
            const numbers: string[] = []

            const searchNumbers = (data: any) => {
                if (typeof data === 'string') {
                    const str = data
                    const matches = str.match(regex)
                    if (matches) {
                        numbers.push(...matches)
                    }
                } else if (typeof data === 'object' && data !== null) {
                    Object.values(data).forEach(searchNumbers)
                }
            }

            searchNumbers(data)
            return Array.from(new Set(numbers))
        }

        const getTranscription = async () => {
            setIsLoading(true)
            setActiveTab('summary')
            const responseData = await API.getCallTranscription(UUID) || {}
            const data = responseData.data

            const isEmptyObject = typeof data === 'string' && data.trim() === '{}'
            if (!data || isEmptyObject) {
                setTranscription(null)
                setIsLoading(false)
                return
            }

            const transcriptionData = JSON.parse(data)
            const numbers = findE164Numbers(transcriptionData)

            for (const foundNumber of numbers) {
                const displayName = await contactContext.getDisplayName?.(foundNumber)
                if (displayName) {
                    numbersToContactMap.set(foundNumber, displayName)
                }
            }

            setTranscription(filteredContent(transcriptionData) as Record<string, any>)
            setNumbersToContactMapState(numbersToContactMap)
            setIsLoading(false)
        }

        getTranscription()
    }, [UUID])

    if (isLoading) return <CallDetailsTabsSkeleton />
    if (!transcription) return null

    return <div className={classes.root}>
        <Tabs
            value={activeTab}
            onChange={setActiveTab}
            items={Object.entries(tabs).map(([key, { text }]) => ({ value: key, text }))}
            className={'tabs'}
        />

        <div>
            {
                activeTab === 'summary' && <CallSummary summary={transcription?.summary} />
            }
            {
                activeTab === 'transcript' && <CallTranscript transcription={transcription?.transcription_object || {}} time={call.start_time} getContactName={getContactName} doesOtherContactExist={doesOtherContactExist} isOtherContact={isOtherContact} />
            }
        </div>

    </div>
}

export default CallDetailsTabs
